import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Alert, Badge, Tooltip } from 'flowbite-react';
import { HiExclamationCircle, HiInformationCircle, HiTrash, HiX, HiQuestionMarkCircle } from 'react-icons/hi';
import { staffApi, type StaffAssignment } from '../../api/staff';
import { personsApi } from '../../api/persons';
import { useAuth } from '../../contexts/AuthContext';
import { dateInputBounds, validateDateField } from '../../utils/dateValidation';

interface ManageContactStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  personId: number;
  personName: string;
  staffId: number;
  onUpdate: () => void;
}

interface CascadePreview {
  personId: number;
  personName: string;
  willDelete: {
    emails: number;
    phones: number;
    addresses: number;
    identificationCodes: number;
    staffRecord: boolean;
    staffAssignments: number;
    userAccount: boolean;
  };
}

// Format date for display
const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Format date for input
const formatDateForInput = (dateStr?: string): string => {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
};

// Get today's date in YYYY-MM-DD format
const getTodayString = (): string => {
  return new Date().toISOString().split('T')[0];
};

// Check if an assignment is currently active
const isAssignmentActive = (assignment: StaffAssignment): boolean => {
  if (!assignment.endDate) return true;
  const endDate = new Date(assignment.endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return endDate > today;
};

// Get assignment type display info
const getAssignmentTypeInfo = (assignment: StaffAssignment): { label: string; type: 'primary' | 'role' } => {
  if (assignment.positionTitle) {
    return { label: assignment.positionTitle, type: 'primary' };
  }
  if (assignment.roleName) {
    return { label: assignment.roleName, type: 'role' };
  }
  return { label: 'No position/role', type: 'primary' };
};

export const ManageContactStatusModal: React.FC<ManageContactStatusModalProps> = ({
  isOpen,
  onClose,
  personId,
  personName,
  staffId,
  onUpdate
}) => {
  const { currentUser, isSuperAdmin, organizationIds } = useAuth();
  const [assignments, setAssignments] = useState<StaffAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [updatingIds, setUpdatingIds] = useState<Set<number>>(new Set());
  const [cascadePreview, setCascadePreview] = useState<CascadePreview | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load staff data and assignments
  useEffect(() => {
    if (isOpen && staffId) {
      loadStaffData();
    }
  }, [isOpen, staffId]);

  const loadStaffData = async () => {
    try {
      setLoading(true);
      setError(null);
      const staff = await staffApi.get(staffId);
      setAssignments(staff.assignments || []);
    } catch (err) {
      console.error('Failed to load staff data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load staff data');
    } finally {
      setLoading(false);
    }
  };

  // Load cascade preview for superAdmin
  useEffect(() => {
    if (isOpen && isSuperAdmin && personId) {
      loadCascadePreview();
    }
  }, [isOpen, isSuperAdmin, personId]);

  const loadCascadePreview = async () => {
    try {
      const preview = await personsApi.getCascadePreview(personId);
      setCascadePreview(preview);
    } catch (err) {
      console.error('Failed to load cascade preview:', err);
    }
  };

  // Filter assignments based on user role
  const visibleAssignments = useMemo(() => {
    if (isSuperAdmin) {
      return assignments;
    }

    // OrgAdmin: If organizationIds is not set, show all (backend already filtered by permissions)
    if (!organizationIds || organizationIds.length === 0) {
      return assignments;
    }

    // OrgAdmin: Only show assignments for their organizations
    return assignments.filter(a => organizationIds.includes(a.educationOrganizationId));
  }, [assignments, isSuperAdmin, organizationIds]);

  // Count active assignments
  const activeCount = useMemo(() => {
    return visibleAssignments.filter(isAssignmentActive).length;
  }, [visibleAssignments]);

  // Handle ending a single assignment
  const handleEndAssignment = async (assignmentId: number) => {
    try {
      setUpdatingIds(prev => new Set(prev).add(assignmentId));
      setError(null);
      await staffApi.updateAssignmentDates(assignmentId, { endDate: getTodayString() });
      setSuccess('Assignment ended successfully');
      await loadStaffData();
      onUpdate();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to end assignment');
    } finally {
      setUpdatingIds(prev => {
        const next = new Set(prev);
        next.delete(assignmentId);
        return next;
      });
    }
  };

  // Handle updating assignment dates
  const handleUpdateDate = async (assignmentId: number, field: 'beginDate' | 'endDate', value: string) => {
    // These inputs save on change, so a year still being typed ("25" for 2025)
    // would otherwise reach the API as 0025 before the user finishes.
    const dateError = validateDateField(value, field === 'beginDate' ? 'Start date' : 'End date');
    if (dateError) {
      setError(dateError);
      return;
    }

    try {
      setUpdatingIds(prev => new Set(prev).add(assignmentId));
      setError(null);
      await staffApi.updateAssignmentDates(assignmentId, { [field]: value || null });
      await loadStaffData();
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update date');
    } finally {
      setUpdatingIds(prev => {
        const next = new Set(prev);
        next.delete(assignmentId);
        return next;
      });
    }
  };

  // Handle clearing end date (reactivating)
  const handleClearEndDate = async (assignmentId: number) => {
    try {
      setUpdatingIds(prev => new Set(prev).add(assignmentId));
      setError(null);
      await staffApi.updateAssignmentDates(assignmentId, { endDate: null });
      setSuccess('Assignment reactivated');
      await loadStaffData();
      onUpdate();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear end date');
    } finally {
      setUpdatingIds(prev => {
        const next = new Set(prev);
        next.delete(assignmentId);
        return next;
      });
    }
  };

  // Handle ending all assignments
  const handleEndAllAssignments = async () => {
    try {
      setUpdatingIds(new Set(visibleAssignments.map(a => a.assignmentId)));
      setError(null);

      // For orgAdmin, only end assignments for their orgs
      const orgIds = isSuperAdmin ? undefined : organizationIds;
      await staffApi.endAllAssignments(staffId, orgIds);

      setSuccess(`All ${isSuperAdmin ? '' : 'accessible '}assignments ended`);
      await loadStaffData();
      onUpdate();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to end assignments');
    } finally {
      setUpdatingIds(new Set());
    }
  };

  // Handle permanent deletion
  const handleDeletePerson = async () => {
    try {
      setDeleting(true);
      setError(null);
      await personsApi.deletePerson(personId);
      setShowDeleteConfirm(false);
      onUpdate();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete person');
      setDeleting(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    setShowDeleteConfirm(false);
    setCascadePreview(null);
    onClose();
  };

  return (
    <Modal show={isOpen} onClose={handleClose} dismissible size="3xl">
      <div className="flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b flex-shrink-0">
          <h3 className="text-xl font-medium text-gray-900">
            Manage Contact: {personName}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
              {error}
            </Alert>
          )}

          {success && (
            <Alert color="success" onDismiss={() => setSuccess(null)} className="mb-4">
              {success}
            </Alert>
          )}

          {/* Info message */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg mb-6">
            <HiInformationCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Deactivating contacts</p>
              <p className="mb-2">To remove a contact from the directory, set an end date on their assignment(s).
                 This preserves historical records while hiding them from active lists.</p>
              <div className="flex flex-col gap-1.5 text-xs border-t border-blue-200 pt-2 mt-2">
                <span className="flex items-start gap-1.5">
                  <Badge color="purple" size="xs" className="flex-shrink-0 mt-0.5">Position</Badge>
                  <span>Custom position title at their organization (e.g., "Director of HR and Communications")</span>
                </span>
                <span className="flex items-start gap-1.5">
                  <Badge color="indigo" size="xs" className="flex-shrink-0 mt-0.5">Role Tag</Badge>
                  <span>Standardized role for searching/filtering (e.g., "Human Resources Director")</span>
                </span>
              </div>
            </div>
          </div>

          {/* Assignments Table */}
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading assignments...</div>
          ) : visibleAssignments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No assignments found for this contact.
            </div>
          ) : (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">
                  Assignments ({activeCount} active / {visibleAssignments.length} total)
                </h4>
                {activeCount > 0 && (
                  <button
                    onClick={handleEndAllAssignments}
                    disabled={updatingIds.size > 0}
                    className="px-3 py-1.5 text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg disabled:opacity-50"
                  >
                    End All Assignments
                  </button>
                )}
              </div>

              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-xs min-w-[700px]">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Organization</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">Position / Role</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-700">Begin Date</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-700">End Date</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-700">Status</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-700 w-20">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {visibleAssignments.map(assignment => {
                      const isActive = isAssignmentActive(assignment);
                      const isUpdating = updatingIds.has(assignment.assignmentId);
                      const typeInfo = getAssignmentTypeInfo(assignment);

                      return (
                        <tr key={assignment.assignmentId} className={isActive ? '' : 'bg-gray-50'}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{assignment.organizationName}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Badge
                                color={typeInfo.type === 'primary' ? 'purple' : 'indigo'}
                                size="xs"
                              >
                                {typeInfo.type === 'primary' ? 'Position' : 'Role Tag'}
                              </Badge>
                              <span className="text-gray-700">{typeInfo.label}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <input
                              type="date"
                              value={formatDateForInput(assignment.beginDate)}
                              onChange={(e) => handleUpdateDate(assignment.assignmentId, 'beginDate', e.target.value)}
                              disabled={isUpdating}
                              {...dateInputBounds}
                              className="px-2 py-1 text-sm border rounded focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                            />
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <input
                                type="date"
                                value={formatDateForInput(assignment.endDate)}
                                onChange={(e) => handleUpdateDate(assignment.assignmentId, 'endDate', e.target.value)}
                                disabled={isUpdating}
                                {...dateInputBounds}
                                className="px-2 py-1 text-sm border rounded focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                              />
                              {assignment.endDate && (
                                <button
                                  onClick={() => handleClearEndDate(assignment.assignmentId)}
                                  disabled={isUpdating}
                                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                                  title="Clear end date (reactivate)"
                                >
                                  <HiX className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge color={isActive ? 'success' : 'gray'} size="sm">
                              {isActive ? 'Active' : 'Ended'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {isActive && (
                              <button
                                onClick={() => handleEndAssignment(assignment.assignmentId)}
                                disabled={isUpdating}
                                className="px-2 py-1 text-xs font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 rounded disabled:opacity-50"
                              >
                                {isUpdating ? '...' : 'End'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Danger Zone - SuperAdmin Only */}
          {isSuperAdmin && (
            <div className="border-t pt-6 mt-6">
              <div className="flex items-center gap-2 mb-4">
                <HiExclamationCircle className="w-5 h-5 text-red-600" />
                <h4 className="font-medium text-red-800">Danger Zone</h4>
              </div>

              {!showDeleteConfirm ? (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 mb-3">
                    Permanently delete this person? This action cannot be undone and will remove all associated records.
                  </p>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
                  >
                    <HiTrash className="w-4 h-4" />
                    Delete Person Permanently
                  </button>
                </div>
              ) : (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="font-medium text-red-800 mb-3">Confirm Deletion</p>

                  {cascadePreview && (
                    <div className="mb-4 text-sm text-red-700">
                      <p className="font-medium mb-2">This will permanently delete:</p>
                      <ul className="list-disc list-inside space-y-1 ml-2">
                        {cascadePreview.willDelete.emails > 0 && (
                          <li>{cascadePreview.willDelete.emails} email address(es)</li>
                        )}
                        {cascadePreview.willDelete.phones > 0 && (
                          <li>{cascadePreview.willDelete.phones} phone number(s)</li>
                        )}
                        {cascadePreview.willDelete.addresses > 0 && (
                          <li>{cascadePreview.willDelete.addresses} address(es)</li>
                        )}
                        {cascadePreview.willDelete.identificationCodes > 0 && (
                          <li>{cascadePreview.willDelete.identificationCodes} identification code(s)</li>
                        )}
                        {cascadePreview.willDelete.staffRecord && (
                          <li>Staff record</li>
                        )}
                        {cascadePreview.willDelete.staffAssignments > 0 && (
                          <li>{cascadePreview.willDelete.staffAssignments} staff assignment(s)</li>
                        )}
                        {cascadePreview.willDelete.userAccount && (
                          <li>User login account</li>
                        )}
                      </ul>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={deleting}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeletePerson}
                      disabled={deleting}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
                    >
                      <HiTrash className="w-4 h-4" />
                      {deleting ? 'Deleting...' : 'Yes, Delete Permanently'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 pt-4 border-t flex justify-end gap-2 flex-shrink-0">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
};
